import { type TimezonesEnum } from '@webapp/core/entities/external/enums/TimezonesEnum'
import { useProcurifyIntl, useFeatureFlag } from '@webapp/hooks'
import { type BudgetIntervalType } from '@webapp/models/enums/BudgetInterval'
import messages from './messages'
import { BudgetCategoryCard } from '../BudgetCategoryCard'
import { SpendTrackerCategoryCardStyled } from './styles/SpendTrackerCategoryCardStyled'

export interface ISpendTrackerCategoryCardProps {
  name: string
  startDate: Date
  endDate: Date
  currentSpend: number | string
  currentRequest: number | string
  otherPendingRequests: number | string
  spendAfterApproval: number | string
  departments?: string[]
  accountCodes?: string[]
  budget?: number | string
  budgetBreakdown?: any
  timeZoneTooltip: TimezonesEnum
  period?: Omit<BudgetIntervalType, 'none'> | null
  variance?: number | string
  variancePercentage?: number | string
  isOverBudget?: boolean
}

export const SpendTrackerCategoryCard = ({
  name,
  startDate,
  endDate,
  currentSpend,
  currentRequest,
  otherPendingRequests,
  spendAfterApproval,
  departments,
  accountCodes,
  budget,
  budgetBreakdown = [],
  timeZoneTooltip,
  period,
  variance,
  variancePercentage,
  isOverBudget,
}: ISpendTrackerCategoryCardProps) => {
  const intl = useProcurifyIntl()
  const FT_5817_ENABLE_BUDGETS_REDESIGN = useFeatureFlag(
    'FT_5817_ENABLE_BUDGETS_REDESIGN'
  )

  const [
    currentSpendBreakdown = { color: 'darkGreen' },
    currentRequestBreakdown = { color: 'darkGreen' },
    otherPendingRequestBreakdown = { color: 'yellow' },
  ] = budgetBreakdown

  return (
    <SpendTrackerCategoryCardStyled>
      <BudgetCategoryCard>
        <BudgetCategoryCard.Header
          name={name}
          startDate={startDate}
          endDate={endDate}
          budgetBreakdown={budget ? budgetBreakdown : null}
          shouldShowBudgetPeriod
          period={period}
          departments={departments}
          accountCodes={accountCodes}
        />
        <BudgetCategoryCard.DetailsBreakdown
          ariaLabel={intl.formatMessage(
            FT_5817_ENABLE_BUDGETS_REDESIGN
              ? messages.budgetBreakdownTableLabel
              : messages.categoryBreakdownTableLabel
          )}
        >
          {budget ? (
            <BudgetCategoryCard.DetailsBreakdown.Row
              name={intl.formatMessage(messages.budget)}
              amount={budget}
            />
          ) : null}
          <BudgetCategoryCard.DetailsBreakdown.Row
            name={intl.formatMessage(messages.currentSpend)}
            amount={currentSpend}
            iconColor={
              budgetBreakdown.length ? currentSpendBreakdown?.color : null
            }
          />
          <BudgetCategoryCard.DetailsBreakdown.Row
            name={intl.formatMessage(messages.currentRequest)}
            amount={currentRequest}
            iconColor={
              budgetBreakdown.length ? currentRequestBreakdown?.color : null
            }
            iconStriped
          />
          <BudgetCategoryCard.DetailsBreakdown.Row
            name={
              !!budget
                ? intl.formatMessage(messages.remainingAfterApproval)
                : intl.formatMessage(messages.spendAfterApproval)
            }
            amount={!!budget ? variance : spendAfterApproval}
            highlight
            danger={isOverBudget}
            variancePercentage={variancePercentage}
          />
        </BudgetCategoryCard.DetailsBreakdown>
        <BudgetCategoryCard.GranularSpend
          ariaLabel={intl.formatMessage(messages.granularSpendTableLabel)}
        >
          <BudgetCategoryCard.GranularSpend.Row
            name={intl.formatMessage(messages.otherPendingRequests)}
            amount={otherPendingRequests}
            iconColor={
              budgetBreakdown.length
                ? otherPendingRequestBreakdown?.color
                : null
            }
          />
        </BudgetCategoryCard.GranularSpend>
      </BudgetCategoryCard>
    </SpendTrackerCategoryCardStyled>
  )
}

SpendTrackerCategoryCard.displayName = 'SpendTrackerCategoryCard'
